import React, { FC, useEffect, useMemo } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Select, Row, Col, Typography, Image, Tooltip, Divider } from 'antd';
import { T } from '@app/components';
import { compose } from '@reduxjs/toolkit';
import { connect } from 'react-redux';
import { injectSaga, injectReducer } from 'redux-injectors';
import { createStructuredSelector } from 'reselect';
import saga from './saga';
import reducer, { getGames, setPaginationParams, setLeagueFilter, updateGameOpenMarketStatus } from './reducer';
import {
  selectCurrentLeague,
  selectCurrentPage,
  selectError,
  selectGames,
  selectIsLoading,
  selectPageSize,
  selectTotalCount
} from './selectors';
import { EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { requestGetLeague } from '../ConfigContainer/reducer';
import { selectGetLeagueLoading, selectLeague } from '../ConfigContainer/selectors';
import moment from 'moment';
import { VISIBLE_LEAGUES } from '@app/utils/constants';

const { Title, Text } = Typography;

const DetailContainer = styled.div`
  padding: 16px;
`;

const HeaderCell = styled(Col)`
  align-content: center;
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid #f0f0f0;
  padding: 8px;
  color: #000;
`;

const DataCell = styled(Col)`
  align-content: center;
  border: 1px solid #f0f0f0;
  padding: 8px;
  color: #000;
`;

const HeaderRow = styled(Row)``;
const DataRow = styled(Row)``;

const OpenMarketContainer: FC = ({
  dispatchGetGames,
  dispatchSetPaginationParams,
  dispatchSetLeagueFilter,
  dispatchGetLeague,
  dispatchUpdateGameOpenMarketStatus,
  games,
  isLoading,
  currentPage,
  pageSize,
  totalCount,
  currentLeague,
  //
  leagues
}) => {
  useEffect(() => {
    dispatchGetGames();
    dispatchGetLeague();
  }, []);

  // Table columns configuration
  const columns = [
    {
      title: 'Home Team',
      key: 'homeTeam',
      render: (game: any) => (
        <Space>
          <Image width={30} height={30} src={game.homeTeamLogo} />
          <Text strong>{game.homeTeam}</Text>
        </Space>
      )
    },
    {
      title: 'Away Team',
      key: 'awayTeam',
      render: (game: any) => (
        <Space>
          <Image width={30} height={30} src={game.awayTeamLogo} />
          <Text strong>{game.awayTeam}</Text>
        </Space>
      )
    },
    {
      title: 'Sport',
      dataIndex: 'sport',
      key: 'sport',
      render: (sport: string) => <Tag color="blue">{sport?.toUpperCase()}</Tag>
    },
    {
      title: 'League',
      dataIndex: 'league',
      key: 'league',
      render: (league: string) => (
        <Space>
          <Tag key={league} color="cyan">
            {league.toUpperCase()}
          </Tag>
        </Space>
      )
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (startDate: string) => moment(startDate).format('MMM DD, YYYY HH:mm')
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      render: (record: any) => (
        <Space>
          {record.openBettingEnabled && (
            <Tooltip title="View Details">
              <Button type="text" icon={<EyeOutlined />} onClick={() => handleViewGame(record)} />
            </Tooltip>
          )}

          <Button
            disabled={record.openBettingEnabled}
            type="primary"
            onClick={() => {
              handleActionButton(record._id, true);
            }}
          >
            {record.openBettingEnabled ? 'Enabled' : 'Enable'}
          </Button>
        </Space>
      )
    }
  ];

  const handleViewGame = (game: any) => {
    const openBettingBookData = game?.openBettingBook?.find((book) => book.market === 'Spread');
    if (!openBettingBookData) return;

    Modal.info({
      title: `Open Market Details:`,
      width: '60%',
      content: (
        <DetailContainer>
          {/* Title */}
          <HeaderRow>
            <HeaderCell span={6} style={{ borderRadius: '6px 0px 0px 0px' }}>
              Team's
            </HeaderCell>
            <HeaderCell span={9}>Home</HeaderCell>
            <HeaderCell span={9} style={{ borderRadius: '0px 6px 0px 0px' }}>
              Away
            </HeaderCell>
          </HeaderRow>

          {/* Name */}
          <DataRow>
            <HeaderCell span={6}>Name</HeaderCell>
            <DataCell span={9}>
              <Space gutter={12}>
                <Image src={game.homeTeamLogo} width={50} height={50} />
                {game.homeTeam}
              </Space>
            </DataCell>
            <DataCell span={9}>
              <Space gutter={12}>
                <Image src={game.awayTeamLogo} width={50} height={50} />
                {game.awayTeam}
              </Space>
            </DataCell>
          </DataRow>

          {/* Market Spread */}
          <HeaderRow>
            <HeaderCell span={6}>Market Spread</HeaderCell>
            <DataCell span={9}>{openBettingBookData.homeTeam.marketPrice}</DataCell>
            <DataCell span={9}>{openBettingBookData.awayTeam.marketPrice}</DataCell>
          </HeaderRow>

          {/* Orders Placed */}
          <HeaderRow>
            <HeaderCell span={6}>Orders Placed</HeaderCell>
            <DataCell span={9}>{openBettingBookData.homeTeam.ordersCount}</DataCell>
            <DataCell span={9}>{openBettingBookData.awayTeam.ordersCount}</DataCell>
          </HeaderRow>

          {/* Posted Amount */}
          <HeaderRow>
            <HeaderCell span={6}>Posted Amount</HeaderCell>
            <DataCell span={9}>{openBettingBookData.homeTeam.postedAmount['$numberDecimal']}</DataCell>
            <DataCell span={9}>{openBettingBookData.awayTeam.postedAmount['$numberDecimal']}</DataCell>
          </HeaderRow>

          {/* Filled Amount */}
          <HeaderRow>
            <HeaderCell span={6}>Filled Amount</HeaderCell>
            <DataCell span={9}>{openBettingBookData.homeTeam.matchedAmount['$numberDecimal']}</DataCell>
            <DataCell span={9}>{openBettingBookData.awayTeam.matchedAmount['$numberDecimal']}</DataCell>
          </HeaderRow>

          {/* Pending Amount */}
          <HeaderRow>
            <HeaderCell span={6} style={{ borderRadius: '0px 0px 0px 6px' }}>
              Pending Amount
            </HeaderCell>
            <DataCell span={9}>{openBettingBookData.homeTeam.pendingAmount['$numberDecimal']}</DataCell>
            <DataCell span={9} style={{ borderRadius: '0px 0px 6px 0px' }}>
              {openBettingBookData.awayTeam.pendingAmount['$numberDecimal']}
            </DataCell>
          </HeaderRow>

          <Divider orientation="left">Book Summary</Divider>

          {/* Orders Count */}
          <HeaderRow>
            <HeaderCell span={6} style={{ borderRadius: '6px 0px 0px 0px' }}>
              Orders Count
            </HeaderCell>
            <DataCell span={18} style={{ borderRadius: '0px 6px 0px 0px' }}>
              {openBettingBookData.bookSummary.ordersCount}
            </DataCell>
          </HeaderRow>

          {/* Posted Amount */}
          <HeaderRow>
            <HeaderCell span={6}>Posted Amount</HeaderCell>
            <DataCell span={18}>{openBettingBookData.bookSummary.postedAmount['$numberDecimal']}</DataCell>
          </HeaderRow>

          {/* Filled Amount */}
          <HeaderRow>
            <HeaderCell span={6}>Filled Amount</HeaderCell>
            <DataCell span={18}>{openBettingBookData.bookSummary.matchedAmount['$numberDecimal']}</DataCell>
          </HeaderRow>

          {/* Pending Amount */}
          <HeaderRow>
            <HeaderCell span={6} style={{ borderRadius: '0px 0px 0px 6px' }}>
              Pending Amount
            </HeaderCell>
            <DataCell span={18} style={{ borderRadius: '0px 0px 6px 0px' }}>
              {openBettingBookData.bookSummary.pendingAmount['$numberDecimal']}
            </DataCell>
          </HeaderRow>
        </DetailContainer>
      )
    });
  };

  const handleActionButton = (gameId: string, enabled: boolean) => {
    Modal.confirm({
      title: 'Confirm',
      content: `Are you sure you want to enable the game for Open Market?`,
      onOk: () => {
        dispatchUpdateGameOpenMarketStatus({ gameId, enabled });
      }
    });
  };

  const leaguesFiltered = useMemo(() => {
    if (!leagues || leagues.length <= 0) {
      return [];
    }
    return leagues.filter((league) => VISIBLE_LEAGUES.includes(league.leagueName));
  }, [leagues]);

  return (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <T id="open_market" />
            </Title>
          </Col>
          <Col>
            <Space>
              <Select
                value={currentLeague}
                style={{ width: 150 }}
                onChange={(value) => {
                  dispatchSetLeagueFilter(value);
                  dispatchGetGames();
                }}
              >
                {leaguesFiltered.map((league) => (
                  <Select.Option key={league.leagueName} value={league.leagueName}>
                    {league.leagueName}
                  </Select.Option>
                ))}
              </Select>
              <Button icon={<ReloadOutlined />} onClick={dispatchGetGames} loading={isLoading}>
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <Table
        columns={columns}
        dataSource={games}
        loading={isLoading}
        rowKey="_id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          onChange: (page, size) => {
            dispatchSetPaginationParams(page, size);
            dispatchGetGames();
          },
          onShowSizeChange: (current, size) => {
            dispatchSetPaginationParams(current, size);
            dispatchGetGames();
          }
        }}
      />
    </Card>
  );
};

const mapStateToProps = createStructuredSelector({
  games: selectGames(),
  isLoading: selectIsLoading(),
  error: selectError(),
  currentPage: selectCurrentPage(),
  pageSize: selectPageSize(),
  totalCount: selectTotalCount(),
  currentLeague: selectCurrentLeague(),
  leagues: selectLeague(),
  getLeagueLoading: selectGetLeagueLoading()
});

export function mapDispatchToProps(dispatch: any) {
  return {
    dispatchGetGames: () => dispatch(getGames()),
    dispatchSetPaginationParams: (page: number, pageSize: number) => dispatch(setPaginationParams({ page, pageSize })),
    dispatchSetLeagueFilter: (status: string) => dispatch(setLeagueFilter(status)),
    dispatchGetLeague: () => dispatch(requestGetLeague({})),
    dispatchUpdateGameOpenMarketStatus: ({ gameId, enabled }: { gameId: string; enabled: boolean }) =>
      dispatch(updateGameOpenMarketStatus({ gameId, enabled }))
  };
}
const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(
  withConnect,
  injectReducer({ key: 'gamesContainer', reducer }),
  injectSaga({ key: 'gamesContainer', saga })
)(OpenMarketContainer);
